import type { ItemMaterial, ProductPublicDto } from "@sticker-giant/sg-frontend-common";
import type { PdpStore } from "@/stores/usePdpStore";
import { usePdpStore } from "@/stores/usePdpStore";
import { getDimensionError } from "./customSizeValidation";
import { getSizeConstraints } from "./size";

export const ROLL_QUANTITY_ERROR = "Please select a new roll quantity";

export type ValidationErrors = Record<string, string>;

interface SizeDimensionCheck {
  width: string | undefined;
  height: string | undefined;
  materialSku: ItemMaterial | undefined;
  productAdmin: ProductPublicDto;
}

function getSizeDimensionError({ width, height, materialSku, productAdmin }: SizeDimensionCheck): string | undefined {
  if (!width || !height) return undefined;
  const materialOption = productAdmin.materialOptions.find(option => option.sku === materialSku);
  const constraints = getSizeConstraints(materialOption, productAdmin);
  return getDimensionError(Number(width), Number(height), constraints);
}

interface SkuFieldCheck {
  key: string;
  storeValue: string | undefined;
  fallback: string | undefined;
  options: { sku: string }[];
  message: string;
}

export function soleOptionSku<T extends { sku: string }>(options: T[]): T["sku"] | undefined {
  return options.length === 1 ? options[0].sku : undefined;
}

export function isValidOption(value: string | undefined, options: { sku: string }[]): boolean {
  return !!value && options.some(option => option.sku === value);
}

// Adhesive and laminate options can be narrowed per material. The product default applies only
// if the chosen material allows it; otherwise the material's sole option is the default.
export function resolveMaterialScopedFields(productAdmin: ProductPublicDto, materialSku: ItemMaterial | undefined) {
  const material = productAdmin.materialOptions.find(option => option.sku === materialSku);
  const adhesiveOptions = material?.adhesiveOptions ?? productAdmin.adhesiveOptions;
  const laminateOptions = material?.laminateOptions ?? productAdmin.laminateOptions;
  return {
    adhesiveOptions,
    laminateOptions,
    adhesiveDefault: isValidOption(productAdmin.defaultAdhesive, adhesiveOptions)
      ? productAdmin.defaultAdhesive
      : soleOptionSku(adhesiveOptions),
    laminateDefault: isValidOption(productAdmin.defaultLaminate, laminateOptions)
      ? productAdmin.defaultLaminate
      : soleOptionSku(laminateOptions),
  };
}

function getSkuFieldChecks(state: PdpStore, productAdmin: ProductPublicDto): SkuFieldCheck[] {
  const { adhesiveOptions, adhesiveDefault, laminateOptions, laminateDefault } = resolveMaterialScopedFields(
    productAdmin,
    state.material ?? productAdmin.defaultMaterial,
  );

  return [
    {
      key: "material",
      storeValue: state.material,
      fallback: productAdmin.defaultMaterial ?? soleOptionSku(productAdmin.materialOptions),
      options: productAdmin.materialOptions,
      message: "Please select a material",
    },
    {
      key: "shape",
      storeValue: state.shape,
      fallback: productAdmin.defaultShape ?? soleOptionSku(productAdmin.shapeOptions),
      options: productAdmin.shapeOptions,
      message: "Please select a shape",
    },
    {
      key: "finish",
      storeValue: state.finish,
      fallback: productAdmin.defaultFinishing ?? soleOptionSku(productAdmin.finishingOptions),
      options: productAdmin.finishingOptions,
      message: "Please select a finish",
    },
    {
      key: "laminate",
      storeValue: state.laminate,
      fallback: laminateDefault,
      options: laminateOptions,
      message: "Please select a lamination",
    },
    {
      key: "adhesive",
      storeValue: state.adhesive,
      fallback: adhesiveDefault,
      options: adhesiveOptions,
      message: "Please select an adhesive",
    },
    {
      key: "orientation",
      storeValue: state.orientation,
      fallback: productAdmin.defaultOrientation ?? soleOptionSku(productAdmin.orientationOptions),
      options: productAdmin.orientationOptions,
      message: "Please select a roll finishing option",
    },
  ];
}

function getSizeError(state: PdpStore, productAdmin: ProductPublicDto): string | undefined {
  const resolvedShape = state.shape ?? productAdmin.defaultShape;
  const selectedShapeOption = productAdmin.shapeOptions.find(s => s.sku === resolvedShape);
  const hasSizeOptions = (selectedShapeOption?.sizeOptions ?? []).length > 0;

  if (hasSizeOptions && (!state.width || !state.height)) {
    return "Please select a size";
  }

  const resolvedMaterial = state.material ?? productAdmin.defaultMaterial;
  return getSizeDimensionError({
    width: state.width,
    height: state.height,
    materialSku: resolvedMaterial,
    productAdmin,
  });
}

export function validatePdpForm(productAdmin: ProductPublicDto): ValidationErrors {
  const state = usePdpStore.getState();
  const errors: ValidationErrors = {};

  for (const { key, storeValue, fallback, options, message } of getSkuFieldChecks(state, productAdmin)) {
    if (options.length <= 1) continue;
    const resolved = storeValue ?? fallback;
    if (!isValidOption(resolved, options)) {
      errors[key] = message;
    }
  }

  if (!state.quantity) {
    errors.quantity = "Please select a quantity";
  }

  const sizeError = getSizeError(state, productAdmin);
  if (sizeError) {
    errors.size = sizeError;
  }

  if (state.rollDriver && state.rollCount === null) {
    errors.rollQuantity = ROLL_QUANTITY_ERROR;
  }

  return errors;
}
